import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { useCreateVideo } from 'queries/video.query'

const initialFormData = {
  title: '',
  type: 'youtube', // 'youtube' or 'promo'
  youtube_url: '',
  video_file: null,
  broadcast_time: '',
  is_active: true,
}

export function CreateVideoModal({ isOpen, onClose }) {
  const { mutate: createVideo, isPending } = useCreateVideo()
  const toast = useToast()
  const fileInputRef = useRef(null)

  const [formData, setFormData] = useState(initialFormData)

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [isOpen])

  const handleClose = () => {
    setFormData(initialFormData)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onClose()
  }

  const MAX_VIDEO_SIZE = 20 * 1024 * 1024 // 20MB

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target
    if (type === 'file') {
      const file = files[0]
      if (file && file.size > MAX_VIDEO_SIZE) {
        toast({
          title: 'File too large',
          description: `The selected file (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the 20MB limit.`,
          status: 'error',
          duration: 4000,
        })
        e.target.value = ''
        setFormData(prev => ({ ...prev, [name]: null }))
        return
      }
      setFormData(prev => ({ ...prev, [name]: file }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const submitData = new FormData()
    submitData.append('title', formData.title)
    submitData.append('type', formData.type)
    submitData.append('is_active', formData.is_active ? 1 : 0)

    if (formData.type === 'youtube') {
      if (!formData.youtube_url) {
        toast({ title: 'YouTube URL is required', status: 'error', duration: 3000 })
        return
      }
      submitData.append('youtube_url', formData.youtube_url)
    } else {
      if (!formData.video_file) {
        toast({ title: 'Video file is required for promo', status: 'error', duration: 3000 })
        return
      }
      if (formData.video_file.size > MAX_VIDEO_SIZE) {
        toast({
          title: 'File too large',
          description: `The video file (${(formData.video_file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the 20MB maximum limit.`,
          status: 'error',
          duration: 4000
        })
        return
      }
      submitData.append('video_file', formData.video_file)
      if (formData.broadcast_time) {
        submitData.append('broadcast_time', formData.broadcast_time)
      }
    }

    createVideo(submitData, {
      onSuccess: () => {
        toast({ title: 'Video created successfully', status: 'success', duration: 3000 })
        handleClose()
      },
      onError: (err) => {
        toast({
          title: 'Error creating video',
          description: err.response?.data?.message || err.message,
          status: 'error',
          duration: 3000
        })
      }
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New Video</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form id="create-video-form" onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input name="title" value={formData.title} onChange={handleChange} />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Video Type</FormLabel>
                <Select name="type" value={formData.type} onChange={handleChange}>
                  <option value="youtube">YouTube Link</option>
                  <option value="promo">Promo Video (Upload)</option>
                </Select>
              </FormControl>

              {formData.type === 'youtube' && (
                <FormControl isRequired>
                  <FormLabel>YouTube URL</FormLabel>
                  <Input type="url" name="youtube_url" value={formData.youtube_url} onChange={handleChange} placeholder="https://youtube.com/watch?v=..." />
                </FormControl>
              )}

              {formData.type === 'promo' && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Video File (mp4, mov &mdash; Max 20MB)</FormLabel>
                    <Input ref={fileInputRef} type="file" name="video_file" accept="video/mp4,video/x-m4v,video/*" onChange={handleChange} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Broadcast Time (Optional)</FormLabel>
                    <Input type="datetime-local" name="broadcast_time" value={formData.broadcast_time} onChange={handleChange} />
                  </FormControl>
                </>
              )}

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Active</FormLabel>
                <Switch name="is_active" isChecked={formData.is_active} onChange={handleChange} colorScheme="brand" />
              </FormControl>
            </VStack>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>Cancel</Button>
          <Button colorScheme="brand" type="submit" form="create-video-form" isLoading={isPending}>Save Video</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
